import React from 'react';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';

// Module Components
import FloatingRegion from './FloatingRegion';
import InfoRegion from './InfoRegion';
import PhotoRegion from './PhotoRegion';

// Selectors
import {
  selectCardCssRules,
  selectFloatingBlocks,
  selectInfoRows,
  selectIsFieldEnabled,
  selectPhotoBlocksByAnchor,
} from '../../../selectors';

// State and Styling
import { getFieldConfiguration } from '../../../state';
import { composeCardStylesheet } from '../../../styling';

// Constants
import {
  DEFAULT_IMAGE_FIT,
  IMAGE_FIELD_ID,
  PREVIEW_SCOPE_CLASS,
  STRINGS,
} from '../../../constants';

// Types
import { FieldId, ProductCard, RegionId } from '../../../types';

interface CardPreviewProps {
  card: ProductCard;
  /**
   * The block carrying the selection outline — the editor's one inspector target,
   * so the pane and the preview cannot disagree about what is being edited
   * (spec R55, R63).
   */
  selectedFieldId: FieldId | null;
  /** Which region the in-flight drag would land in, or null when idle. */
  activeRegion: RegionId | null;
  isDragging: boolean;
  onSelectBlock: (fieldId: FieldId) => void;
  onDeleteBlock: (fieldId: FieldId) => void;
  onClearSelection: () => void;
}

/**
 * The live preview: three named drop regions composing the card the shopper
 * would see (spec R48).
 *
 * Selecting a block outlines it *and* opens that element's properties column beside
 * the preview (spec R55, R63) — one target, two entry points.
 *
 * Clicking anywhere that is not a block clears the selection — blocks stop the
 * click from bubbling, so this one handler is the whole of "clicking elsewhere
 * on the preview deselects" (spec R58).
 *
 * "Deselects" is not "selects nothing", though: with no element targeted the
 * properties column shows the CARD's own properties (orientation, footer), so
 * the card is then the subject. Since 2026-09-29 the card says so — it wears the
 * same accent ring a selected block does. The panel and the preview are supposed
 * to be one fact shown twice, and until then the card half of that fact was
 * invisible: clicking the card's whitespace swapped the whole right-hand column
 * with nothing on the card to say why.
 *
 * It is also the **only** place in the module that renders a `<style>` element: the
 * card's per-element custom CSS, composed into one already-scoped stylesheet by
 * `styling/` and rendered as that element's text child (spec R67, R68, ADR-4A). The
 * scope class sits on the card wrapper, so every generated selector is bounded by
 * the card whatever the user typed — no `innerHTML`, no `insertRule`, nothing
 * injected into `<head>`.
 */
const CardPreview = ({
  card,
  selectedFieldId,
  activeRegion,
  isDragging,
  onSelectBlock,
  onDeleteBlock,
  onClearSelection,
}: CardPreviewProps) => {
  const isHorizontal = card.orientation === 'horizontal';
  // No element targeted means the CARD is what the properties column is showing —
  // the same condition `CardEditor` branches the panel on. Kept as a named value
  // so the ring below and that branch are visibly the one rule.
  const isCardSelected = selectedFieldId === null;
  const configurationFor = (fieldId: FieldId) => getFieldConfiguration(card, fieldId);
  // Recomposed on every keystroke in the Custom styling input — which is what "applied
  // live to preview" means, and why the CSS text is length-capped (NFR A3).
  const cardStylesheet = composeCardStylesheet(selectCardCssRules(card));

  return (
    <section className="tw-flex tw-flex-col tw-gap-3 tw-p-4 tw-bg-surface-sunken tw-rounded-md">
      <header className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-2">
        <Typography variant="title-header" as="h3" className="tw-text-ink">
          {STRINGS.preview.title}
        </Typography>
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-tertiary">
          {STRINGS.preview.hint}
        </Typography>
      </header>

      <div className="tw-flex tw-justify-center">
        <div
          onClick={onClearSelection}
          className={`${PREVIEW_SCOPE_CLASS} tw-bg-surface tw-border tw-rounded-md tw-p-2 tw-max-w-full ${
            isHorizontal ? 'tw-w-[520px]' : 'tw-w-[320px]'
          } ${
            /* No `ring-offset`, unlike `PreviewBlock`: the card already has a
               1px border, and an offset ring left a white hairline between the
               two accent lines that read as a rendering fault rather than a
               selection. Border and ring flush make one solid 3px edge. */
            isCardSelected
              ? 'tw-border-accent tw-ring-2 tw-ring-accent'
              : 'tw-border-edge'
          }`}
        >
          {/* The card's own custom CSS. A text child, inside the scope wrapper, and
              empty when no element carries custom CSS of its own (spec R67, R68). */}
          <style>{cardStylesheet}</style>

          <div className={isHorizontal ? 'tw-flex tw-flex-row tw-gap-3' : 'tw-flex tw-flex-col'}>
            <PhotoRegion
              blocksByAnchor={selectPhotoBlocksByAnchor(card)}
              imageEnabled={selectIsFieldEnabled(card, IMAGE_FIELD_ID)}
              imageFit={configurationFor(IMAGE_FIELD_ID).imageFit ?? DEFAULT_IMAGE_FIT}
              orientation={card.orientation}
              isDragging={isDragging}
              activeRegion={activeRegion}
              selectedFieldId={selectedFieldId}
              configurationFor={configurationFor}
              onSelectBlock={onSelectBlock}
              onDeleteBlock={onDeleteBlock}
            />

            <InfoRegion
              rows={selectInfoRows(card)}
              isDragging={isDragging}
              activeRegion={activeRegion}
              selectedFieldId={selectedFieldId}
              configurationFor={configurationFor}
              onSelectBlock={onSelectBlock}
              onDeleteBlock={onDeleteBlock}
            />
          </div>

          <FloatingRegion
            blocks={selectFloatingBlocks(card)}
            footerEnabled={card.footerEnabled}
            isDragging={isDragging}
            activeRegion={activeRegion}
            selectedFieldId={selectedFieldId}
            configurationFor={configurationFor}
            onSelectBlock={onSelectBlock}
            onDeleteBlock={onDeleteBlock}
          />
        </div>
      </div>
    </section>
  );
};

export default CardPreview;
